/* eslint-disable no-constant-condition */
// external
import React from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

// components
import { IconInfo } from '@americanexpress/dls-react';
import CurrencyInput from 'src/components/formik-fields/currency-input/CurrencyInput';
import BasicInput from 'src/components/formik-fields/basic-input/BasicInput';
import ManualAccountService from 'src/services/manualAccount.service';

// styles
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import FormatService from 'src/services/format.service';
import { AddAccountFormViewModel } from 'src/types/view-models/addAccount/addAccount.view.model';
import LiteralsService from 'src/services/literals/LiteralsService';
import DefaultFormButtons from '../../defaultFormButtons/DefaultFormButtons';
import { InvestmentHoldingType } from 'src/types/investment-holding.model';
import TickerInput from 'src/components/formik-fields/ticker-input/TickerInput';

// styles
import colors from 'src/styles/colors.module.scss';
import bgColors from 'src/styles/bgColors.module.scss';
import formStyles from '../../addAccountFormContainer.module.scss';
import styles from './mutualFundsAndPublicStocks.module.scss';
import Card from 'src/components/atomic/atoms/card/Card';
import { DLSSize } from 'src/types/dls-size.enum';
import { DateTimeFormatter, LocalDate, LocalDateTime } from '@js-joda/core';

const isToday = (localDateTime: LocalDateTime): boolean => {
    const today = LocalDate.now();
    return localDateTime.toLocalDate().toString() === today.toString();
};

/** This component does not use AddAccountForm because it requires access to the form values for rendering */
export default function MutualFundsAndPublicStocksForm({
    manualAccount,
    onSubmit,
    handleBack
}: AddAccountFormViewModel): JSX.Element {
    const secondaryOnclick = (): void => {
        void ManualAccountService.deleteAccount(manualAccount);
        handleBack();
    };
    return (
        <Formik
            initialValues={{
                ...{
                    name: '',
                    ticker: '',
                    price: '',
                    quantity: '',
                    lastUpdated: ''
                },
                ...manualAccount?.investmentHolding
            }}
            validationSchema={Yup.object({
                name: Yup.string().required(LiteralsService.getLiteral('Onboarding.forms.inputValidationNull')),
                ticker: Yup.string().required(LiteralsService.getLiteral('Onboarding.forms.inputValidationNull')),
                price: Yup.number()
                    .required(LiteralsService.getLiteral('Onboarding.forms.inputValidationNull'))
                    .typeError(LiteralsService.getLiteral('Onboarding.forms.inputValidationTypeNumber')),
                quantity: Yup.number().required(LiteralsService.getLiteral('Onboarding.forms.inputValidationNull'))
            })}
            onSubmit={(values) => {
                void onSubmit(values, manualAccount, false, InvestmentHoldingType.Stock);
            }}
        >
            {({ values }) => {
                const lastUpdated: LocalDateTime = values.lastUpdated
                    ? LocalDateTime.parse(new Date(values.lastUpdated).toISOString().slice(0, -1))
                    : undefined;
                const total: number = Number(values.price) * Number(values.quantity);
                return (
                    <Form className={formStyles.form}>
                        <BasicInput id="name" label={LiteralsService.getLiteral('Onboarding.forms.inputLabels.name')} />
                        <TickerInput
                            id="ticker"
                            label={LiteralsService.getLiteral('Onboarding.forms.inputLabels.ticker')}
                        />
                        <CurrencyInput
                            id="price"
                            label={LiteralsService.getLiteral('Onboarding.forms.inputLabels.price')}
                        />
                        {values.lastUpdated && (
                            <Card
                                className={`${styles.info} ${bgColors.neutralWeak}`}
                                icon={<IconInfo size={DLSSize.MEDIUM} />}
                                heading={
                                    <Typography variant={TypographyVariants.BODY} component="p">
                                        {LiteralsService.getLiteral('Onboarding.forms.priceLastUpdated')}
                                        {isToday(lastUpdated)
                                            ? LiteralsService.getLiteral('Onboarding.forms.today')
                                            : `${LiteralsService.getLiteral(
                                                  'Onboarding.forms.on'
                                              )} ${lastUpdated.format(DateTimeFormatter.ofPattern('M/d/yyyy'))}`}
                                        {LiteralsService.getLiteral('Onboarding.forms.at')}
                                        {FormatService.formatTime(lastUpdated, true)}
                                    </Typography>
                                }
                            >
                                <></>
                            </Card>
                        )}
                        <BasicInput
                            id="quantity"
                            label={LiteralsService.getLiteral('Onboarding.forms.inputLabels.quantity')}
                        />
                        {total > 0 && (
                            <div>
                                <Typography variant={TypographyVariants.BODY_BOLD}>
                                    {LiteralsService.getLiteral('Onboarding.forms.total')}
                                </Typography>
                                <Typography variant={TypographyVariants.HEADING_XL} className={colors.textStrong}>
                                    {FormatService.format(total)}
                                </Typography>
                            </div>
                        )}
                        <DefaultFormButtons update={!!manualAccount} secondaryOnclick={secondaryOnclick} />
                    </Form>
                );
            }}
        </Formik>
    );
}
